import { forwardRef, useState } from 'react';

import {
  ApiAutomaticSensor,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import { Stack } from '@mui/material';
import { getMeasureTypesFromDeviceType } from '../../../../store/config';
import { DataState } from '../../../../store/data/state';
import { useForm } from '../../../../utils/hooks/useForm';
import DropdownStringSelector from '../../../utils/MUISpecifics/dropDownStringSelector/DropDownStringSelector';
import DropdownItemsSelector from '../../../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import TextInput from '../../../utils/MUISpecifics/inputText/TextInput';
import { useTranslation } from 'react-i18next';

interface Step1Props {
  deviceTypeMeasureTypes: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >;
  device_types: ApiReturnDeviceType[];
  measureTypes: DataState<string, { [KEY in number]: ApiReturnMeasureType }>;
  models: string[];
  form: ReturnType<typeof useForm<ApiAutomaticSensor>>;
}

const defaultValues: ApiAutomaticSensor = {
  name: '',
  brand: '',
  type: 'automatic_sensor',
  device_type_id: null,
  sn: null,
};

function Step1({
  form,
  device_types,
  deviceTypeMeasureTypes,
  measureTypes,
  models,
}: Step1Props): JSX.Element {
  // use translation here :
  const { t } = useTranslation('components.devices');

  const { setValue, watch, onSubmit, getErrors, hasSubmitted } = form;

  const getDeviceTypeById = (id: number) => {
    return device_types.find((dt) => dt.id === id);
  };

  const defaultDeviceType =
    (getDeviceTypeById(watch('device_type_id')) as ApiReturnDeviceType) ?? null;
  const defaultMeasures =
    (defaultDeviceType &&
      getMeasureTypesFromDeviceType(
        defaultDeviceType,
        deviceTypeMeasureTypes,
        measureTypes
      )) ??
    [];

  const [filtredProposition, setFiltredProposition] = useState<{
    deviceType: ApiReturnDeviceType | null;
    measureTypes: ApiReturnMeasureType[];
  }>({
    deviceType: defaultDeviceType,
    measureTypes: defaultMeasures,
  });

  const updateMeasures = (id: number) => {
    if (id !== null) {
      const deviceType = getDeviceTypeById(id);
      const measures = getMeasureTypesFromDeviceType(
        deviceType!,
        deviceTypeMeasureTypes,
        measureTypes
      );
      setValue('device_type_id', id);
      setFiltredProposition({
        deviceType: deviceType!,
        measureTypes: measures,
      });
    } else {
      setValue('device_type_id', defaultValues.device_type_id);
      setFiltredProposition({ deviceType: null, measureTypes: [] });
    }
  };

  return (
    <form onSubmit={onSubmit} className="form" noValidate>
      <Stack spacing={2}>
        <TextInput
          onChange={(value) => setValue('name', value)}
          defaultValue={watch('name')}
          label={t('Nom')}
          placeholder={t('Entrer un nom...')}
          required
          errors={
            hasSubmitted
              ? { tag: 'Submited', errors: getErrors('name') }
              : { tag: 'Not submitted' }
          }
          name={''}
        />
        <DropdownItemsSelector
          onSelect={(value) => updateMeasures(value!)}
          defaultValue={watch('device_type_id')}
          values={device_types}
          label={t('Type du capteur connecté')}
          disabled={false}
          name={'device_type_id'}
          required
          errors={
            hasSubmitted
              ? {
                  tag: 'Submited',
                  errors: getErrors('device_type_id'),
                }
              : { tag: 'Not submitted' }
          }
        />
        {filtredProposition.measureTypes.length !== 0 && (
          <div
            className="uk-margin"
            style={{
              backgroundColor: '#00000008',
              borderRadius: '5px',
              padding: '15px',
            }}
          >
            <div>Types de mesures</div>
            <div
              className="uk-margin"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {filtredProposition.measureTypes.map((mt) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'start',
                    gap: '10px',
                  }}
                >
                  <span
                    style={{
                      backgroundColor: '#d3f7b0',
                      padding: '5px',
                      borderRadius: '5px',
                    }}
                  >
                    {mt.name}
                  </span>
                  <span
                    style={{
                      backgroundColor: '#f7b0ba',
                      padding: '5px',
                      borderRadius: '5px',
                    }}
                  >
                    {mt.unite}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DropdownStringSelector
          onSelect={(value) => setValue('brand', value)}
          defaultValue={watch('brand')}
          values={models}
          label={t('Modèle')}
          disabled={false}
          name={'device_type_id'}
          required
          errors={
            hasSubmitted
              ? { tag: 'Submited', errors: getErrors('brand') }
              : { tag: 'Not submitted' }
          }
        />
        <TextInput
          name={'sn'}
          defaultValue={watch('sn')!}
          onChange={(value) => setValue('sn', value)}
          label={t('Numéro de série')}
          placeholder={t('Entrer un numéro de série...')}
          required
          errors={
            hasSubmitted
              ? { tag: 'Submited', errors: getErrors('sn') }
              : { tag: 'Not submitted' }
          }
        />
      </Stack>
    </form>
  );
}

export default forwardRef(Step1);
